// User Level Progress Component for Memara
import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  useActivityTimeline,
  type ActivityFeedScope,
} from '../hooks/useActivityTimeline';
import { Star, Flame, TrendingUp, Calendar, Award, Zap } from 'lucide-react';

interface UserLevelProgressProps {
  showHeader?: boolean;
  compact?: boolean;
  className?: string;
  /** When `account`, activity feed is user-wide (e.g. Settings Progress). */
  activityFeedScope?: ActivityFeedScope;
}

const UserLevelProgress: React.FC<UserLevelProgressProps> = ({ 
  showHeader = true, 
  compact = false,
  className = '',
  activityFeedScope = 'contextSpace',
}) => {
  const { t } = useTranslation('common');
  const { userStats, activitySummary, isLoading } = useActivityTimeline({
    activityFeedScope,
  });

  const getStreakColor = (streak: number) => {
    if (streak >= 30) return 'text-red-500';
    if (streak >= 14) return 'text-orange-500';
    if (streak >= 7) return 'text-yellow-500';
    if (streak >= 3) return 'text-blue-500';
    return 'text-gray-500';
  };

  const getStreakEmoji = (streak: number) => {
    if (streak >= 30) return '🔥';
    if (streak >= 14) return '⚡';
    if (streak >= 7) return '💫';
    if (streak >= 3) return '✨';
    return '🌟';
  };

  const getLevelTitle = (level: number) => {
    if (level >= 50) return 'Memory Master';
    if (level >= 30) return 'Knowledge Keeper';
    if (level >= 20) return 'Memory Expert';
    if (level >= 10) return 'Memory Collector';
    if (level >= 5) return 'Memory Explorer';
    return 'Memory Novice';
  };

  const experiencePercentage = (userStats.experience / userStats.nextLevelExp) * 100;

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {showHeader && (
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Level Progress</h3>
          </div>
        )}
        <div className="animate-pulse space-y-3">
          <div className="h-20 bg-gray-200 rounded-lg"></div>
          <div className="h-16 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {showHeader && (
        <div className="flex items-center gap-2">
          <Star className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Level Progress</h3>
        </div>
      )}

      {/* Main Level Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 right-4 text-6xl">⭐</div>
          <div className="absolute bottom-4 left-4 text-4xl">🎯</div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-8xl opacity-5">
            🧠
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-3xl font-bold">Level {userStats.level}</h2>
              <p className="text-blue-100 text-sm">{getLevelTitle(userStats.level)}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl mb-1">⭐</div>
              <div className="text-xs text-blue-100">
                {userStats.experience} / {userStats.nextLevelExp} XP
              </div>
            </div>
          </div>

          {/* Experience Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Experience to next level</span>
              <span>{Math.round(experiencePercentage)}%</span>
            </div>
            <div className="w-full bg-blue-400 bg-opacity-30 rounded-full h-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${experiencePercentage}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                className="bg-white h-3 rounded-full shadow-sm"
              />
            </div>
            <div className="text-xs text-blue-100">
              {userStats.nextLevelExp - userStats.experience} XP needed for Level {userStats.level + 1}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className={`grid gap-4 ${compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
        {/* Current Streak */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <Flame className={`w-5 h-5 ${getStreakColor(userStats.currentStreak)}`} />
            <span className="text-sm font-medium text-gray-600">Current Streak</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{userStats.currentStreak}</span>
            <span className="text-lg">{getStreakEmoji(userStats.currentStreak)}</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {userStats.currentStreak === 0 
              ? 'Start your streak today!' 
              : `${userStats.currentStreak} day${userStats.currentStreak === 1 ? '' : 's'} strong`
            }
          </div>
        </motion.div>

        {/* Longest Streak */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <Award className="w-5 h-5 text-yellow-500" />
            <span className="text-sm font-medium text-gray-600">Best Streak</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{userStats.longestStreak}</span>
            <span className="text-lg">🏆</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Personal record
          </div>
        </motion.div>

        {/* Today's Activity */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5 text-green-500" />
            <span className="text-sm font-medium text-gray-600">Today</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{activitySummary.todayActivities}</span>
            <span className="text-lg">📅</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Activities completed
          </div>
        </motion.div>

        {/* Weekly Activity */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-gray-600">This Week</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{activitySummary.weekActivities}</span>
            <span className="text-lg">📊</span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {t('userLevelProgress.activitiesThisWeek')}
          </div>
        </motion.div>
      </div>

      {/* Achievements Summary */}
      {!compact && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200"
        >
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-yellow-600" />
            <span className="font-medium text-gray-900">
              {t('userLevelProgress.achievementProgressTitle')}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-xl font-bold text-yellow-600">{activitySummary.unlockedAchievements}</div>
              <div className="text-xs text-gray-600">{t('userLevelProgress.achievementsLabel')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-green-600">{activitySummary.reachedMilestones}</div>
              <div className="text-xs text-gray-600">{t('userLevelProgress.milestonesLabel')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600">{userStats.totalMemories}</div>
              <div className="text-xs text-gray-600">{t('userLevelProgress.memoriesTrackedLabel')}</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-purple-600">{userStats.totalSearches}</div>
              <div className="text-xs text-gray-600">{t('userLevelProgress.searchesLabel')}</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      {!compact && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="flex flex-wrap gap-2"
        >
          <button className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors">
            <Zap className="w-4 h-4" />
            Keep streak going!
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors">
            <Star className="w-4 h-4" />
            View achievements
          </button>
        </motion.div>
      )}

      {/* Member Since */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className="text-center text-sm text-gray-500"
      >
        Member since {userStats.joinedDate.toLocaleDateString('en-US', { 
          month: 'long', 
          year: 'numeric' 
        })}
      </motion.div>
    </div>
  );
};

export default UserLevelProgress; 